import React, { useCallback } from "react";

import { faEllipsisH } from "@fortawesome/pro-solid-svg-icons/faEllipsisH";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import SortModel from "@mapmycustomers/shared/types/viewModel/internalModel/SortModel";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, Dropdown } from "@mapmycustomers/ui";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import DownloadMultipleEntitiesModal from "@app/component/DownloadMultipleEntitiesModal";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import DownloadEntitiesMapping from "@app/types/DownloadEntitiesMapping";
import { AreaSearchQuery } from "@app/types/filters/AreaSearchQuery";

import CustomizeMapSortModal from "../CustomizeMapSortModal";

export interface CustomizeMenuProps {
  analyticsKeyPath?: string | string[];
  onChangeSort?: (sort: SortModel, query?: AreaSearchQuery) => void;
  onDownloadRecords?: (entityColumnsMap: DownloadEntitiesMapping) => void;
  sort: SortModel;
  sortQuery?: AreaSearchQuery;
  visibleEntities?: EntityTypesSupportedByMapsPage[];
}

const CustomizeMenu = ({
  analyticsKeyPath = "Records List",
  onChangeSort,
  onDownloadRecords,
  sort,
  sortQuery,
  visibleEntities,
}: CustomizeMenuProps) => {
  const intl = useIntl();

  const [visible, show, hide] = useBoolean(false);
  const [sortVisible, showSort, hideSort] = useBoolean(false);
  const [downloadModal, showDownloadModal, hideDownloadModal] = useBoolean();
  const handleDownloadRecords = useCallback(
    (entityColumnsMap: DownloadEntitiesMapping) => {
      onDownloadRecords?.(entityColumnsMap);
    },
    [onDownloadRecords]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        show();
      } else {
        hide();
      }
    },
    [hide, show]
  );

  const handleShowSort = useCallback(() => {
    if (!sortVisible) {
      showSort();
    }
  }, [showSort, sortVisible]);

  const handleSortVisibleChange = useCallback(
    (open: boolean) => {
      if (!open) {
        hideSort();
      } else {
        showSort();
      }
    },
    [hideSort, showSort]
  );

  const handleShowCustomize = useCallback(() => {
    window.open(`${Path.SETTINGS}/${SettingPath.PINNED_FIELDS}`, "_blank");
    hide();
  }, [hide]);

  return (
    <ExtendedAnalytics keyPath={analyticsKeyPath}>
      <Dropdown
        menu={{
          items: [
            {
              key: "sort",
              label: (
                <Popover
                  content={
                    sortVisible ? (
                      <CustomizeMapSortModal
                        onChange={onChangeSort}
                        onHide={hideSort}
                        sort={sort}
                        sortQuery={sortQuery}
                      />
                    ) : null
                  }
                  destroyTooltipOnHide
                  getPopupContainer={(trigger: HTMLElement) => trigger.parentElement!}
                  onOpenChange={handleSortVisibleChange}
                  open={sortVisible}
                  placement="bottomLeft"
                  trigger="click"
                >
                  {intl.formatMessage({
                    id: "recordList.customization.menu.sort",
                    defaultMessage: "Sort",
                    description: "View Menu - Sort menu item title",
                  })}
                </Popover>
              ),
              onClick: handleShowSort,
            },
            {
              key: "customize",
              label: intl.formatMessage({
                id: "recordList.customization.menu.pinnedFields",
                defaultMessage: "Pinned Fields",
                description: "View Menu - Pinned Fields menu item title",
              }),
              onClick: handleShowCustomize,
            },
            {
              disabled: !onDownloadRecords,
              key: "export",
              label: intl.formatMessage({
                id: "recordList.customization.menu.download",
                defaultMessage: "Download",
                description: "View Menu - Quick Export menu item title",
              }),
              onClick: showDownloadModal,
            },
          ],
        }}
        onOpenChange={handleOpenChange}
        open={visible}
        placement="bottomRight"
        trigger={["click"]}
      >
        <Tooltip
          title={intl.formatMessage({
            id: "recordList.customization.menu.open.tooltip",
            defaultMessage: "Open menu",
            description: "Open Menu toolbar button text",
          })}
        >
          <Button icon={<FontAwesomeIcon icon={faEllipsisH} size="lg" />} type="control" />
        </Tooltip>
      </Dropdown>
      {downloadModal && visibleEntities && (
        <DownloadMultipleEntitiesModal
          hasSorting
          onDownload={handleDownloadRecords}
          onHide={hideDownloadModal}
          visibleEntities={visibleEntities}
        />
      )}
    </ExtendedAnalytics>
  );
};

export default CustomizeMenu;
